import { EventExternalLink, SaleState } from '@packages/types';

const dateTimeFormatter = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
});

const dayFormatter = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const numericDateFormatter = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const parseDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const formatEventDateTime = (
  startsAt?: string | null,
  endsAt?: string | null,
  startTimeLabel?: string | null,
  endTimeLabel?: string | null,
) => {
  const start = parseDate(startsAt);
  if (!start) return null;

  const end = parseDate(endsAt);
  const sLabel = startTimeLabel?.trim();
  const eLabel = endTimeLabel?.trim();

  // No custom labels: preserve the original formatting exactly.
  if (!sLabel && !eLabel) {
    if (!end) return dateTimeFormatter.format(start);
    if (sameDay(start, end)) {
      return `${dayFormatter.format(start)}, ${timeFormatter.format(start)}–${timeFormatter.format(
        end,
      )}`;
    }
    return `${dateTimeFormatter.format(start)} – ${dateTimeFormatter.format(end)}`;
  }

  // Label path: substitute the label(s) for the time-of-day.
  const startTime = sLabel ? sLabel : timeFormatter.format(start);
  if (!end) return `${dayFormatter.format(start)} um ${startTime}`;
  const endTime = eLabel ? eLabel : timeFormatter.format(end);
  if (sameDay(start, end)) {
    return `${dayFormatter.format(start)}, ${startTime}–${endTime}`;
  }
  return `${dayFormatter.format(start)} um ${startTime} – ${dayFormatter.format(
    end,
  )} um ${endTime}`;
};

export const formatEventNumericDate = (value?: string | null) => {
  const date = parseDate(value);
  return date ? numericDateFormatter.format(date) : null;
};

export const safeHttpUrl = (value?: string | null) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
};

export const safeEventLink = (link: EventExternalLink) => {
  const url = safeHttpUrl(link.url);
  if (!url) return null;

  const label = link.label?.trim() || new URL(url).hostname;
  return { label, url };
};

export const saleStateCopy = (saleState: SaleState) => {
  switch (saleState) {
    case 'draft':
    case 'scheduled':
      return {
        title: 'Ticketverkauf noch nicht geöffnet',
        message:
          'Der Ticketverkauf für diese Veranstaltung startet bald. Schau später wieder vorbei.',
      };
    case 'closed':
      return {
        title: 'Online-Vorverkauf geschlossen',
        message: 'Der Online-Vorverkauf für diese Veranstaltung ist beendet.',
      };
    case 'soldOut':
      return {
        title: 'Ausverkauft',
        message: 'Für diese Veranstaltung sind aktuell keine Tickets mehr verfügbar.',
      };
    case 'open':
      return {
        title: 'Tickets',
        message: 'Der Ticketverkauf ist geöffnet.',
      };
  }
};
